import { AuthService } from "../service/auth.service";
import { Injectable, Injector } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { NotificationService } from "@core/service/notification.service";
import { TranslateService } from "@ngx-translate/core";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private translateService!: TranslateService;
  constructor(
    private authenticationService: AuthService,
    private notificationService: NotificationService,
    private injector: Injector
  ) {}

  private getTranslateService(): TranslateService {
    if (!this.translateService) {
      this.translateService = this.injector.get(TranslateService);
    }
    return this.translateService;
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    // Skip error handling for Keycloak requests to avoid login loops
    if (request.url.includes('/realms/') || request.url.includes('/protocol/openid-connect/')) {
      return next.handle(request);
    }

    // Check if this request should suppress error notifications
    const suppressErrorNotification = request.headers.has('X-Suppress-Error-Notification');

    return next.handle(request).pipe(
      catchError((err) => {
        // Skip notification if suppressed
        if (suppressErrorNotification) {
          console.warn('Suppressed error notification:', err);
          return throwError(() => err);
        }

        let errorMsg = '';
        const translateService = this.getTranslateService();
        if (err.status === 401) {
          errorMsg = translateService.instant('MESSAGES.ERROR.UNAUTHORIZED');
          // Don't auto-logout and reload - let the auth guard handle authentication
          // This prevents infinite loops during Keycloak authentication
          console.warn('401 Unauthorized - authentication required');
        } else if (err.status === 403) {
          errorMsg = translateService.instant('MESSAGES.ERROR.FORBIDDEN');
          console.warn('403 Forbidden - access denied');
        } else if(err.status === 400) {
          if (err.error?.errors) {
            const errorMessages = Object.values(err.error.errors).flat();
            errorMsg = errorMessages.join('<br>');
          } else {
            errorMsg = err.error?.Message || translateService.instant('MESSAGES.ERROR.INVALID_DATA');
          }
        } else if (err.status === 404) {
          errorMsg = translateService.instant('MESSAGES.ERROR.NOT_FOUND');;
        } else if (err.status === 423) {
          // Enterprise locked - handled by EnterpriseLockInterceptor
          return throwError(() => err);
        } else {
          errorMsg = translateService.instant('MESSAGES.ERROR.UNEXPECTED');
        }

        this.notificationService.showError(errorMsg);

        const error = errorMsg || err.statusText;
        
        console.error(error);

        return throwError(() => new Error(error));
      })
    );
  }
}
